/**
 * API Service - Frontend HTTP Client
 * ===================================
 * ✅ Enhanced with comprehensive logging for API call tracking
 */

import { createLogger } from '../utils/logger';

const logger = createLogger('src/services/apiService.js');
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class ApiService {
  async request(endpoint, options = {}) {
    const method = options.method || 'GET';
    logger.entry('request', { method, endpoint, hasBody: !!options.body });
    
    try {
      const url = `${API_BASE_URL}${endpoint}`;
      const config = {
        headers: { 'Content-Type': 'application/json', ...options.headers },
        ...options
      };
      
      if (options.body instanceof FormData) {
        delete config.headers['Content-Type'];
        logger.debug('request', 'Using FormData, removed Content-Type header');
      }
      
      logger.info('request', `🌐 Making ${method} request`, { url, endpoint });
      
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok || data?.success === false) {
        logger.error('request', `API request failed`, { 
          status: response.status,
          endpoint,
          message: data?.message 
        });
        throw new Error(data?.message || `HTTP ${response.status}`);
      }
      
      logger.exit('request', { 
        endpoint, 
        status: response.status,
        hasData: !!data
      });
      
      return data?.data ?? data;
    } catch (error) {
      logger.error('request', `API Error for ${endpoint}`, error);
      throw error;
    }
  }

  // ==================== Apps ====================
  async getAllApps() {
    logger.warn('getAllApps', 'Legacy method called - use getAppsByUser instead');
    return this.getAppsByUser('legacy-no-uid');
  }

  async getAppsByUser(uid) {
    logger.entry('getAppsByUser', { uid });
    
    if (!uid) {
      logger.error('getAppsByUser', 'UID is required');
      throw new Error('UID is required');
    }
    
    logger.info('getAppsByUser', 'Fetching apps for user', { uid });
    // Include deleted apps in the response
    const data = await this.request(`/app/list?createdBy=${encodeURIComponent(uid)}&includeDeleted=true`);
    const apps = Array.isArray(data) ? data : (data?.apps || data || []);
    
    // Debug: Log app structure
    if (apps.length > 0) {
      logger.debug('getAppsByUser', 'Sample app structure', apps[0]);
    }
    
    logger.exit('getAppsByUser', { count: apps.length });
    return apps;
  }

  async createApp({ appName, description, createdBy, appType }) {
    logger.entry('createApp', { appName, createdBy, appType });
    
    if (!appName || !createdBy) {
      logger.error('createApp', 'Missing required fields', { appName, createdBy });
      throw new Error('appName and createdBy are required');
    }
    
    logger.info('createApp', 'Creating new app', { appName, createdBy, appType });
    const result = await this.request('/app/create', {
      method: 'POST',
      body: JSON.stringify({ appName, description, createdBy, appType })
    });
    
    logger.exit('createApp', { appPrefix: result?.appPrefix });
    return result;
  }

  /**
   * Soft delete an application (marks as deleted instead of permanent deletion)
   * @param {string} appName - Application name
   * @returns {Promise<boolean>} Success status
   */
  async deleteApp(appName) {
    logger.entry('deleteApp', { appName });
    
    if (!appName) {
      logger.error('deleteApp', 'appName is required');
      throw new Error('appName is required');
    }
    
    logger.info('deleteApp', 'Soft deleting app', { appName });
    const result = await this.request(`/app/${encodeURIComponent(appName)}`, {
      method: 'DELETE'
    });
    
    logger.exit('deleteApp', { success: true });
    return result?.success !== false;
  }

  /**
   * Restore a deleted application
   * @param {string} appName - Application name
   * @returns {Promise<boolean>} Success status
   */
  async restoreApp(appName) {
    logger.entry('restoreApp', { appName });
    
    if (!appName) {
      logger.error('restoreApp', 'appName is required');
      throw new Error('appName is required');
    }
    
    logger.info('restoreApp', 'Restoring deleted app', { appName });
    const result = await this.request(`/app/${encodeURIComponent(appName)}/restore`, {
      method: 'POST'
    });
    
    logger.exit('restoreApp', { success: true });
    return result?.success !== false;
  }

  // ==================== Pages ====================
  // (existing savePage / getPagesByApp / getPage / deletePage unchanged below)

  async savePage(arg1, arg2, arg3) {
    let appName, pageName, pageData;
    if (typeof arg1 === 'string') { appName = arg1; pageName = arg2; pageData = arg3; }
    else if (arg1 && typeof arg1 === 'object') {
      appName  = arg1.appName || arg1?.pageData?.appName;
      pageName = arg1.pageName || arg1?.pageData?.pageName || arg1.docName || arg1.name;
      if (arg1.pageData && typeof arg1.pageData === 'object') pageData = arg1.pageData;
      else {
        const { appName: _a, pageName: _p, docName: _d, savedAt: _s, updatedAt: _u, updatedBy: _by, ...rest } = arg1;
        pageData = rest;
      }
    }
    if (pageData && pageData.pageData && typeof pageData.pageData === 'object') {
      pageData = pageData.pageData;
    }
    if (!appName || !pageName || !pageData) {
      console.error('❌ [apiService.savePage] Missing required fields', { appName, pageName, hasPageData: !!pageData });
      throw new Error('Missing required field(s): appName, pageName, pageData');
    }
    const result = await this.request('/pages', {
      method: 'POST',
      body: JSON.stringify({ appName, pageName, pageData })
    });
    return result;
  }

  async getPagesByApp(appName) {
    const data = await this.request(`/pages/${appName}`);
    return Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  }

  async getPage(appName, pageName) {
    const data = await this.request(`/pages/${appName}/${pageName}`);
    const page = data && data.data ? data.data : data || {};
    return page;
  }

  async deletePage(appName, pageName) {
    return this.request(`/pages/${appName}/${pageName}`, { method: 'DELETE' });
  }

  // ==================== Components ====================
  // Fixed by Claude on 2025-11-12 - Added component API methods to save forms to appname_components
  
  /**
   * Save a component to the library
   * @param {Object} params - Component parameters
   * @param {string} params.appName - Application name (optional if in componentData)
   * @param {Object} params.componentData - Component data including appName, type, fields, etc.
   * @returns {Promise<Object>} - Saved component result
   */
  async saveComponent({ appName, componentData }) {
    logger.entry('saveComponent', { appName: appName || componentData?.appName });
    
    // Ensure appName is in componentData
    const dataToSave = {
      ...componentData,
      appName: componentData.appName || appName
    };
    
    if (!dataToSave.appName) {
      logger.error('saveComponent', 'appName is required');
      throw new Error('appName is required in componentData or as parameter');
    }
    
    logger.info('saveComponent', 'Saving component to library', { 
      appName: dataToSave.appName,
      type: dataToSave.type,
      name: dataToSave.name
    });
    
    const result = await this.request('/components', {
      method: 'POST',
      body: JSON.stringify({ componentData: dataToSave })
    });
    
    logger.exit('saveComponent', { componentId: result?.componentId });
    return result;
  }

  /**
   * Get all components for an app
   * @param {Object} params - Query parameters
   * @param {string} params.appName - Application name
   * @param {string} [params.type] - Filter by component type
   * @param {string} [params.category] - Filter by category
   * @returns {Promise<Array>} - Array of components
   */
  async getAllComponents({ appName, type, category }) {
    logger.entry('getAllComponents', { appName, type, category });
    
    if (!appName) {
      logger.error('getAllComponents', 'appName is required');
      throw new Error('appName is required');
    }
    
    const queryParams = new URLSearchParams({ appName });
    if (type) queryParams.append('type', type);
    if (category) queryParams.append('category', category);
    
    logger.info('getAllComponents', 'Fetching components', { appName, type, category });
    const data = await this.request(`/components?${queryParams.toString()}`);
    const components = Array.isArray(data) ? data : (data?.components || data || []);
    
    logger.exit('getAllComponents', { count: components.length });
    return components;
  }
  // End of fix by Claude on 2025-11-12

  // ==================== Actions ====================
  /**
   * Save an action to the library
   * @param {Object} params - Action parameters
   * @param {string} params.appName - Application name
   * @param {string} params.actionId - Action ID (unique identifier)
   * @param {Object} params.actionData - Action configuration data
   * @returns {Promise<Object>} - Saved action result
   */
  async saveAction({ appName, actionId, actionData }) {
    logger.entry('saveAction', { appName, actionId });
    
    if (!appName || !actionId || !actionData) {
      logger.error('saveAction', 'Missing required fields');
      throw new Error('appName, actionId, and actionData are required');
    }
    
    logger.info('saveAction', 'Saving action to library', { 
      appName,
      actionId,
      actionType: actionData.actionType
    });
    
    const result = await this.request('/actions/save', {
      method: 'POST',
      body: JSON.stringify({ appName, actionId, actionData })
    });
    
    logger.exit('saveAction', { actionId: result?.actionId });
    return result;
  }

  /**
   * Get all actions for an app
   * @param {string} appName - Application name
   * @returns {Promise<Array>} - Array of actions
   */
  async getActions(appName) {
    logger.entry('getActions', { appName });
    
    if (!appName) {
      logger.error('getActions', 'appName is required');
      throw new Error('appName is required');
    }
    
    logger.info('getActions', 'Fetching actions', { appName });
    const data = await this.request(`/actions/${appName}`);
    const actions = Array.isArray(data) ? data : (data?.actions || data || []);
    
    logger.exit('getActions', { count: actions.length });
    return actions;
  }

  /**
   * Get a specific action by ID
   * @param {string} appName - Application name
   * @param {string} actionId - Action ID
   * @returns {Promise<Object>} - Action object
   */
  async getActionById(appName, actionId) {
    logger.entry('getActionById', { appName, actionId });
    
    if (!appName || !actionId) {
      logger.error('getActionById', 'appName and actionId are required');
      throw new Error('appName and actionId are required');
    }
    
    logger.info('getActionById', 'Fetching action', { appName, actionId });
    const data = await this.request(`/actions/${appName}/${actionId}`);
    
    logger.exit('getActionById', { found: !!data });
    return data;
  }

  /**
   * Delete an action from the library
   * @param {string} appName - Application name
   * @param {string} actionId - Action ID
   * @returns {Promise<Object>} - Delete result
   */
  async deleteAction(appName, actionId) {
    logger.entry('deleteAction', { appName, actionId });
    
    if (!appName || !actionId) {
      logger.error('deleteAction', 'appName and actionId are required');
      throw new Error('appName and actionId are required');
    }
    
    logger.info('deleteAction', 'Deleting action', { appName, actionId });
    const result = await this.request(`/actions/${appName}/${actionId}`, {
      method: 'DELETE'
    });
    
    logger.exit('deleteAction', { success: true });
    return result;
  }
}

export default new ApiService();
